import { AppApi } from '@/customApiClients/appApiClient/Api';
import { ManagedAgentResponseModel } from '@/customApiClients/mgmtApiClient/Api';
import { useEffect, useMemo, useState } from 'react';

function getFallbackDescription(agent: ManagedAgentResponseModel) {
    return `${agent.displayName} is a specialized agent with access to your organization's connected apps and data sources.`;
}

// Builtin tool names (see BUILTIN_TOOL_NAMES in agent_admin.py) that grant an
// agent internet access vs. the ability to write/execute code, used to derive
// the two hover-card badges from getAgentInformation's builtinTools map.
const INTERNET_ACCESS_TOOLS = ['web_fetch', 'web_search'];
const CODE_WRITE_ACCESS_TOOLS = ['bash', 'edit', 'write'];

function hasAnyEnabledTool(
    builtinTools: Record<string, boolean> | null,
    toolNames: string[]
): boolean {
    return toolNames.some((name) => builtinTools?.[name]);
}

interface UseAgentInformationOptions {
    // When true (default), the network call is deferred until `load()` is
    // called — used by hover cards that shouldn't fetch until opened. Pass
    // `lazy: false` for surfaces where the info is always on screen (e.g.
    // picker grids), which fetches as soon as the agent is known.
    lazy?: boolean;
}

export type AgentInformation = ReturnType<typeof useAgentInformation>;

/**
 * Fetches a single managed agent's live description and connected sources,
 * shared between AgentInfoSubmenu and ManagedAgentPicker so both surfaces
 * present identical agent info.
 */
export function useAgentInformation(
    agent: ManagedAgentResponseModel,
    { lazy = true }: UseAgentInformationOptions = {}
) {
    const appApi = useMemo(
        () => new AppApi({ baseApiParams: { credentials: 'include' } }),
        []
    );
    const [agentInfo, setAgentInfo] = useState<{
        mcpServerNames: string[];
        description: string | null;
        builtinTools: Record<string, boolean> | null;
    } | null>(null);
    const [loading, setLoading] = useState(false);

    const load = () => {
        if (agentInfo !== null || loading) return;
        setLoading(true);
        appApi.api
            .getAgentInformation(agent.agentId, {
                apiKeySecret: agent.apiKey
            })
            .then((res) =>
                setAgentInfo({
                    mcpServerNames: res?.data?.mcpServerNames ?? [],
                    description: res?.data?.description ?? null,
                    builtinTools: res?.data?.builtinTools ?? null
                })
            )
            .catch(() =>
                setAgentInfo({
                    mcpServerNames: [],
                    description: null,
                    builtinTools: null
                })
            )
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!lazy) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazy, agent.agentId]);

    return {
        load,
        loading,
        mcpServerNames: agentInfo?.mcpServerNames ?? null,
        description:
            agent.userFacingDescription?.trim() ||
            agentInfo?.description ||
            getFallbackDescription(agent),
        hasInternetAccess: hasAnyEnabledTool(
            agentInfo?.builtinTools ?? null,
            INTERNET_ACCESS_TOOLS
        ),
        hasCodeWriteAccess: hasAnyEnabledTool(
            agentInfo?.builtinTools ?? null,
            CODE_WRITE_ACCESS_TOOLS
        )
    };
}
